import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
}

const BlogPreview = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLatestBlogs = async () => {
      setIsLoading(true);
      const data = await safeFetch<any[]>(
        supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .limit(3),
        [],
        'Fetching latest insights'
      );

      const formatted = data.map(post => ({
        slug: post.slug,
        title: post.title,
        excerpt: post.short_description,
        date: post.created_at,
        category: post.tags?.split(',')[0] || 'Strategic Insight',
        image: post.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&auto=format&fit=crop&q=60'
      }));

      setBlogs(formatted);
      setIsLoading(false);
    };

    fetchLatestBlogs();
  }, []);

  return (
    <section className="section-padding bg-secondary/10 relative overflow-hidden">
      <div className="container-max relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-end justify-between gap-8 mb-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em] mb-6">
              Expert Perspectives
            </span>
            <h2 className="heading-lg text-primary">
              Institutional <span className="text-gradient-gold">Market Insights</span>
            </h2>
            <p className="text-muted-foreground font-medium mt-4">Stay ahead with professional analysis and high-probability trading concepts from our core team.</p>
          </div>
          <Link to="/blog">
            <Button variant="outline" className="btn-outline-navy h-14 px-8 text-xs font-black uppercase tracking-widest whitespace-nowrap">
              Explore All Insights
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {isLoading ? (
            <div className="col-span-full py-20 flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-accent" />
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Synchronizing Intelligence...</p>
            </div>
          ) : blogs.length === 0 ? (
            <div className="col-span-full py-20 text-center">
              <p className="text-muted-foreground font-medium">No articles published yet.</p>
            </div>
          ) : (
            blogs.map((post, index) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <article className="card-premium h-full flex flex-col !p-0 bg-white/60 border-white/60 group-hover:bg-white overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-1.5 bg-primary/90 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-widest rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-10 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-6 text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5 text-accent" />
                      <span className="text-[10px] font-black uppercase tracking-widest">
                        {new Date(post.date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>

                    <h3 className="text-xl font-black text-primary mb-4 uppercase tracking-tighter group-hover:text-gold transition-colors duration-300 leading-tight line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-sm text-muted-foreground font-medium line-clamp-2 leading-relaxed mb-8">
                      {post.excerpt}
                    </p>

                    <div className="mt-auto flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary group-hover:text-accent transition-colors duration-300">
                      Read Analysis <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </article>
              </Link>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
